<template>
  <b-modal :id="modalId" size="md" modal-class="modal-manage-view">
    <h5>{{ report.labelName ?? $t(report.label) }}</h5>
    <div class="wrapper p-2">
      <div
        class="mc-modal__filter-item"
        v-if="hasIsUrgentFilter || hasIsImportantFilter || hasIsReadFilter"
      >
        <label>{{ $t('global.action.show') }} {{ $t('global.object.notification') }}</label>
        <div class="d-flex gap-2" v-if="hasIsUrgentFilter">
          <McCheckbox v-model="isUrgent" />
          <span>{{ $t('report.object.isUrgent') }}</span>
        </div>
        <div class="d-flex gap-2 my-2" v-if="hasIsImportantFilter">
          <McCheckbox v-model="isImportant" />
          <span>{{ $t('notification.text.importantNotification') }}</span>
        </div>
        <div class="d-flex gap-2 my-2" v-if="hasIsReadFilter">
          <McCheckbox v-model="unread" />
          <span>
            {{ $t('notification.text.unread') }} ({{
              $t('notification.text.importantNotification')
            }})
          </span>
        </div>
      </div>
      <div class="mc-modal__filter-item" v-if="hasIsResolvedFilter || hasFalseAlert">
        <b-form-group>
          <template #label>
            <div class="d-flex justify-content-between align-items-center">
              <label>{{ $t('report.object.reportType.title') }}</label>
            </div>
          </template>
          <div class="mc-modal__filter-button-group">
            <template v-for="option in statusOptions">
              <button
                :key="`${option.key}-${btnKey}`"
                type="button"
                :class="{ 'mc-modal__filter-button--active': statusCheck[option.key] }"
                class="mc-modal__filter-button"
                @click="filterStatus(option.key)"
              >
                {{ option.label }}
              </button>
            </template>
          </div>
        </b-form-group>
      </div>

      <b-form-group v-if="hasReportTypeFilter">
        <template #label>
          <div class="d-flex justify-content-between align-items-center">
            <label>{{ $t('report.object.reportType.title') }}</label>
          </div>
        </template>
        <b-form-radio v-model="reportType" :value="weeklyReportType" class="me-2">
          <label class="radio-label">{{ $t('report.object.reportType.weekly') }}</label>
        </b-form-radio>
        <b-form-radio v-model="reportType" :value="monthlyReportType" class="me-2">
          <label class="radio-label">{{ $t('report.object.reportType.monthly') }}</label>
        </b-form-radio>
      </b-form-group>
      <div v-if="isReportTypeRequired" class="ms-1 mt-1 fst-italic">
        <span class="text-danger">*</span> {{ $t('report.filters.required') }}
      </div>

      <PeriodFilter
        ref="period-filter"
        v-if="hasPeriodeFilter"
        :report="report"
        @update="updatePeriod($event)"
      />
      <div v-if="isPeriodRequired" class="ms-1 mt-1 fst-italic">
        <span class="text-danger">*</span> {{ $t('report.filters.required') }}
      </div>

      <FormulateForm
        ref="formRef"
        v-model="formValues"
        class="my-1"
        @validation="handleFormValidation"
      >
        <InputTableFilterFleetUtilization
          v-if="checkReportKey('fleet-utilization')"
          :formValues="formValues"
          :fullObjects="fullObjects"
          :report="finalReport"
          :parameter="parameter"
          @update:formValues="updateFormValues($event)"
          @update:fullObjects="updateFullObjects($event)"
        />
        <InputTableFilter
          v-else
          :formValues="formValues"
          :fullObjects="fullObjects"
          :report="finalReport"
          :parameter="parameter"
          @update:formValues="updateFormValues($event)"
          @update:fullObjects="updateFullObjects($event)"
        />
      </FormulateForm>
      <b-form-group v-if="hasIntervalFilter" id="interval-column" label-for="periode" class="p-1">
        <template #label>
          <div class="d-flex justify-content-between align-items-center">
            <label>{{ $t('report.object.intervalData') }}</label>
            <span v-b-tooltip.topleft :title="$t('report.object.intervalTooltip')">
              <i class="icon-outline_info"></i>
            </span>
          </div>
        </template>
        <div class="mc-modal__filter-button-group">
          <template v-for="item in listInterval">
            <button
              :key="`${item.key}-${report.key}`"
              type="button"
              :class="{
                'mc-modal__filter-button--active': intervalData[item.key] && !item.disabled,
                'mc-modal__filter-button': !item.disabled,
                'mc-modal__filter-button--disabled': item.disabled,
              }"
              :disabled="item.disabled"
              @click="filterIntervalData(item.key)"
            >
              {{ $t(item.label) }}
            </button>
          </template>
        </div>
      </b-form-group>

      <b-form-group v-if="hasSummaryChart" id="summary-chart" label-for="summary-chart" class="p-1">
        <div class="mc-modal__filter-button-group mb-2">
          <span class="d-flex gap-2 w-100">
            <McCheckbox v-model="summaryChart" :disabled="disabledSummaryChart" />
            {{ $t('report.object.summaryChart') }}
          </span>
        </div>
        <McAlert
          variant="info"
          :html="$t('report.object.summaryChartDesc')"
          withIcon
          :withGap="false"
        />
      </b-form-group>

      <RitageDuration
        ref="ritage-duration-filter"
        v-if="hasLoadedDurationFilter && hasUnloadedDurationFilter && hasTotalRitageDurationFilter"
        :report="report"
        @update="updateRitageDuration"
      />
      <McAlert
        v-if="hasFalseAlert"
        variant="info"
        :text="$t('report.text.falseAlertdesc')"
        withIcon
      />
      <McAlert
        v-if="checkReportKey('summary-fuel') && hasAccess(['fuel_efficiency_v2:read'])"
        variant="info"
        :html="$t('report.text.fuelEfficiencyInfo')"
        withIcon
        :withGap="false"
      />
      <McAlert
        v-if="checkReportKey('fuel-recapitulation') && reportType == weeklyReportType"
        variant="info"
        :html="$t('report.text.fuelRecapitulationInfo')"
        withIcon
        :withGap="false"
      />
    </div>
    <template #modal-header>
      <b-row>
        <b-col md="8">
          <span class="__title"> {{ title ?? $t('report.title.reportParameter') }} </span>
        </b-col>
        <b-col md="4" v-if="isHaveResetButton">
          <a class="reset-button" href="#" @click.prevent="resetFilters">{{
            $t('global.action.resetFilter')
          }}</a>
        </b-col>
      </b-row>
    </template>
    <template #modal-footer>
      <b-row class="modal__footer mx-0">
        <b-col sm="12" md="12" class="modal__footer-btn">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.cancel')"
            @click="cancelFilter"
          />
          <McButton
            variant="primary"
            :text="$t('global.apply')"
            :disabled="formHasErrors"
            @click="handleAccept"
          />
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import McAlert from '@/components/McAlert'
import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'
import { Helper } from '@/mixin/helper'
import { adas, dms, driverBehavior, general } from '@/router/views/notification/lib/alert-code'
import { GET_VEHICLE_GROUPS, GET_VEHICLE_TYPES } from '@/services/api/master/fleet'
import { NOTIFICATION_LIST_CODES } from '@/services/api/notification/general'
import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment'
import { mapGetters } from 'vuex'
import InputTableFilter from './components/InputTableFilter'
import PeriodFilter from './components/PeriodFilter'
import RitageDuration from './components/RitageDuration'
import intervals from './lib/intervals.js'
import InputTableFilterFleetUtilization from '../../tms/fleet-utilization/components/InputTableFilter'

export default {
  components: {
    InputTableFilter,
    InputTableFilterFleetUtilization,
    McButton,
    McAlert,
    McCheckbox,
    PeriodFilter,
    RitageDuration,
  },
  mixins: [Helper],
  apollo: {
    vehicleTypes: {
      query: GET_VEHICLE_TYPES,
      skip() {
        return !this.isHaveVehicleField
      },
      variables() {
        return {
          filter: {
            hasVehicle: true,
            isActive: true,
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        let vehicleTypes = { ...data.vehicleTypes }
        this.$store.commit('report/updateVehicleTypeList', {
          list: vehicleTypes.vehicleTypes.data,
          loaded: true,
        })
      },
    },
    vgroups: {
      query: GET_VEHICLE_GROUPS,
      skip() {
        return !this.isHaveVehicleField
      },
      result({ data, loading }) {
        if (loading) return
        let vgroups = { ...data.vgroups }
        this.$store.commit('report/updateVgroupList', {
          list: vgroups.vgroups,
          loaded: true,
        })
      },
    },
    alertCodes: {
      query: NOTIFICATION_LIST_CODES,
      skip() {
        return !this.isHaveAlertCodeField
      },
      variables() {
        let alertCodes = [
          12, 1, 10, 2, 11, 8, 4, 5, 19, 20, 13, 14, 15, 16, 21, 22, 23, 24, 25, 26, 75, 17, 18, 3,
        ]
        alertCodes = alertCodes.concat(
          general.reduce((cumm, item) => {
            if (item.scope || item.scope == null) cumm.push(...item.alertCode)
            return cumm
          }, []),
          driverBehavior.reduce((cumm, item) => {
            if (item.scope || item.scope == null) cumm.push(...item.alertCode)
            return cumm
          }, [])
        )
        if (this.hasAccess(['e1c-web-dms:read']))
          alertCodes = alertCodes.concat(
            dms.reduce((cumm, item) => {
              if (item.scope || item.scope == null) cumm.push(...item.alertCode)
              return cumm
            }, [])
          )
        if (this.hasAccess(['e1g-web-adas:read']))
          alertCodes = alertCodes.concat(
            adas.reduce((cumm, item) => {
              if (item.scope || item.scope == null) cumm.push(...item.alertCode)
              return cumm
            }, [])
          )
        alertCodes = [...new Set(alertCodes)]
        return {
          filter: {
            alertCode: alertCodes,
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        let res = { ...data }
        let filteredAlert = res.alertCodes.alertCodes
          .filter((x) => x.alertCode != 37 && x.alertCode != 95)
          .map((item) => {
            return { ...item, id: item.alertCode }
          })
        this.$store.commit('report/updateAlertFilterList', {
          list: filteredAlert,
          loaded: true,
        })
      },
    },
  },
  props: {
    report: {
      type: Object,
      required: true,
    },
    unRedirect: {
      type: Boolean,
      default: false,
    },
    parameter: {
      type: String,
      default: 'filter',
    },
    title: {
      type: String,
      default: null,
    },
    isHaveResetButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['refresh-table'],
  data() {
    return {
      modalId:
        this.parameter == 'additionalFilter'
          ? this.report.modal + '-additional-filter'
          : this.report.modal,
      showModal: false,
      periode: [],
      periodeMonth: null,
      filter: {},
      vehicleData: [],
      driverData: [],
      formValues: {},
      fullObjects: {},
      tempModel: {},
      dateType: null,
      vehicleTypes: {
        vehicleTypes: [],
      },
      vgroups: {
        vgroups: [],
      },
      alertCodes: {
        alertCodes: [],
      },
      intervalData: {
        fiveMinutes: true,
      },
      interval: intervals,
      falseAlert: null,
      summaryChart: false,
      monthlyReportType: 'monthly',
      weeklyReportType: 'weekly',
      reportType: 'weekly',
      weeklyPeriodKey: 1,
      isImportant: false,
      formHasErrors: false,
      isUrgent: false,
      isRead: false,
      unread: false,
      statusCheck: {
        all: true,
      },
      btnKey: 0,
      parameterFilter: this.report[this.parameter] ?? this.report.filter,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),
    isHaveEmployeeField() {
      return this.parameterFilter?.driverId
    },
    isHaveVehicleField() {
      return this.parameterFilter?.vehicleId
    },
    isHaveGeofenceField() {
      return (
        this.parameterFilter?.geofenceId ||
        this.parameterFilter?.garageId ||
        this.parameterFilter?.startGeofenceId ||
        this.parameterFilter?.endGeofenceId ||
        this.parameterFilter?.intersectGeofenceIds ||
        this.parameterFilter?.ritage
      )
    },
    isHaveAlertCodeField() {
      return this.parameterFilter?.alertCode
    },
    isHaveRitageField() {
      return this.parameterFilter?.ritage
    },
    isPeriodRequired() {
      const fieldPeriod = this.parameterFilter?.periode
      if (fieldPeriod) {
        return fieldPeriod.required
      }
      return false
    },
    isReportTypeRequired() {
      const fieldReportType = this.parameterFilter?.reportType
      if (fieldReportType) {
        return fieldReportType.required
      }
      return false
    },
    hasLoadedDurationFilter() {
      return (
        this.parameterFilter &&
        Object.keys(this.parameterFilter).length > 0 &&
        Object.keys(this.parameterFilter).includes('loadedDuration')
      )
    },
    hasUnloadedDurationFilter() {
      return (
        this.parameterFilter &&
        Object.keys(this.parameterFilter).length > 0 &&
        Object.keys(this.parameterFilter).includes('unloadedDuration')
      )
    },
    hasTotalRitageDurationFilter() {
      return (
        this.parameterFilter &&
        Object.keys(this.parameterFilter).length > 0 &&
        Object.keys(this.parameterFilter).includes('totalRitageDuration')
      )
    },
    hasPeriodeFilter() {
      return (
        this.parameterFilter &&
        Object.keys(this.parameterFilter).length > 0 &&
        Object.keys(this.parameterFilter).includes('periode')
      )
    },
    hasIntervalFilter() {
      return (
        this.parameterFilter &&
        Object.keys(this.parameterFilter).length > 0 &&
        Object.keys(this.parameterFilter).includes('interval') &&
        this.hasAccess(['interval_report:read'])
      )
    },
    hasReportTypeFilter() {
      return (
        this.parameterFilter &&
        Object.keys(this.parameterFilter).length > 0 &&
        Object.keys(this.parameterFilter).includes('reportType')
      )
    },
    hasIsImportantFilter() {
      return (
        this.parameterFilter &&
        Object.keys(this.parameterFilter).length > 0 &&
        Object.keys(this.parameterFilter).includes('isImportant')
      )
    },
    hasIsUrgentFilter() {
      return (
        this.parameterFilter &&
        Object.keys(this.parameterFilter).length > 0 &&
        Object.keys(this.parameterFilter).includes('isUrgent') &&
        this.hasAccess(['e4e-alert-center-urgent:read'])
      )
    },
    hasIsResolvedFilter() {
      return (
        this.parameterFilter &&
        Object.keys(this.parameterFilter).length > 0 &&
        Object.keys(this.parameterFilter).includes('isResolved')
      )
    },
    hasIsReadFilter() {
      return (
        this.parameterFilter &&
        Object.keys(this.parameterFilter).length > 0 &&
        Object.keys(this.parameterFilter).includes('isRead')
      )
    },
    intervalFilter() {
      const keys = Object.keys(this.intervalData)
      return keys[0]
    },
    listInterval() {
      let result = this.interval
      if (Object.keys(this.formValues).length <= 0) return result
      else if (this.formValues.vehicleId && this.formValues.vehicleId.length > 1) {
        result = result.map((item) => {
          if (item.key === 'all') {
            return {
              ...item,
              disabled: true,
            }
          } else {
            return item
          }
        })
        return result
      } else return result
    },
    hasCsvScope() {
      return (
        this.report.downloadOptions?.includes('csv') &&
        (this.hasAccess(['csv_report:read']) ||
          (this.hasAccess(['csv_report_plus:read']) && this.checkReportKey('summary-stopped')))
      )
    },
    finalReport() {
      const report = { ...this.report }
      const parameterFilter = report[this.parameter] || report.filter
      if (this.hasCsvScope && parameterFilter.vehicleId) {
        parameterFilter.vehicleId.value = []
      } else if (this.hasCsvScope && !parameterFilter.vehicleId && parameterFilter.driverId) {
        parameterFilter.driverId.value = []
      }
      return report
    },
    hasFalseAlert() {
      return (
        this.parameterFilter &&
        Object.keys(this.parameterFilter).length > 0 &&
        Object.keys(this.parameterFilter).includes('falseAlert') &&
        this.hasAccess(['false_alert_report:read'])
      )
    },
    hasSummaryChart() {
      return (
        this.parameterFilter &&
        Object.keys(this.parameterFilter).length > 0 &&
        Object.keys(this.parameterFilter).includes('summaryChart')
      )
    },
    disabledSummaryChart() {
      if (this.hasIntervalFilter && !this.intervalData?.all) {
        return true
      }

      if (Array.isArray(this.periode) && this.periode.length > 1) {
        const diffInDays = Math.abs(moment(this.periode[0]).diff(moment(this.periode[1]), 'days'))
        if (diffInDays > 7) {
          return true
        }
      }

      return this.formValues.vehicleId?.length > 1
    },
    status() {
      return [
        {
          key: 'unresolved',
          label: this.$t('controlTower.detail.notChecked'),
        },
        {
          key: 'resolved',
          label: this.$t('controlTower.detail.checked'),
        },
        {
          key: 'falseAlert',
          label: 'False Alert',
        },
      ]
    },
    statusOptions() {
      const data = cloneDeep(this.status)
      data.unshift({
        key: 'all',
        label: this.$t('global.filter.all'),
      })
      return data
    },
  },
  watch: {
    formValues(val) {
      if (val.vehicleId && val.vehicleId.length > 1 && this.intervalData.all) {
        this.intervalData = { fiveMinutes: true }
      }
    },
    disabledSummaryChart(newValue) {
      if (newValue) {
        this.summaryChart = false
      }
    },
    reportType(value, oldValue) {
      if (this.$refs['period-filter']) {
        this.$refs['period-filter'].UpdateReportType(value, oldValue)
      }
    },
  },
  mounted() {
    this.filter = this.parameterFilter ?? {}
  },
  methods: {
    updateRitageDuration(value) {
      if (value.loadingDuration) {
        this.formValues.loadingDuration = value.loadingDuration
      } else if (value.unloadingDuration) {
        this.formValues.unloadingDuration = value.unloadingDuration
      } else if (value.totalRitageDuration) {
        this.formValues.totalRitageDuration = value.totalRitageDuration
      } else if (value.loadedTravelDuration) {
        this.formValues.loadedTravelDuration = value.loadedTravelDuration
      } else if (value.unloadedTravelDuration) {
        this.formValues.unloadedTravelDuration = value.unloadedTravelDuration
      } else if (value.stoppedDurationLoaded) {
        this.formValues.stoppedDurationLoaded = value.stoppedDurationLoaded
      } else if (value.stoppedDurationUnloaded) {
        this.formValues.stoppedDurationUnloaded = value.stoppedDurationUnloaded
      }
    },
    updatePeriod(value) {
      this.periode = value.periode
      this.periodeMonth = value.periodeMonth
      this.dateType = value.dateType
    },
    filterStatus(key) {
      if (key == 'all') {
        this.statusCheck = this.statusOptions.reduce((acc, item) => {
          acc[item.key] = false
          return acc
        }, {})
        this.statusCheck.all = true
      } else {
        this.statusCheck.all = false
        this.statusCheck[key] = !this.statusCheck[key]
        let obj = { ...this.statusCheck }
        let objArr = []
        Object.keys(obj).forEach(function (key) {
          if (obj[key]) {
            objArr.push(true)
          }
        })
        if (objArr.length === this.status.length || objArr.length === 0) {
          this.statusCheck = this.statusOptions.reduce((acc, item) => {
            acc[item.key] = false
            return acc
          }, {})
          this.statusCheck.all = true
          return
        }
      }
      this.btnKey += 1
    },
    transformDate(date) {
      const formatDate =
        this.checkReportKey('detail-history-vehicle') ||
        this.checkReportKey('fuel') ||
        this.checkReportKey('temperature') ||
        this.checkReportKey('alert')
          ? 'YYYY-MM-DD HH:mm:ss'
          : 'YYYY-MM-DD'
      return moment(new Date(date)).format(formatDate)
    },
    checkReportKey(key) {
      return this.report.key === key || this.report.reportKey === key
    },
    handleFormValidation() {
      this.formHasErrors = this.$refs.formRef?.hasErrors || false
    },
    async SetFormValues(model) {
      this.tempModel = { ...model }
      let {
        periode,
        periodeMonth,
        dateType,
        fullObjects,
        interval,
        falseAlert,
        reportType,
        summaryChart,
        isImportant,
        isUrgent,
        isResolved,
        isRead,
        ritage,
        ...otherFilter
      } = this.tempModel

      // transform periode to string with format YYYY-MM-DD or YYYY-MM-DD HH:mm:ss based on report key
      if (periode && periode.length > 0)
        this.periode = [this.transformDate(periode[0]), this.transformDate(periode[1])]
      // transform interval to object with key as interval and value as true, if interval is not exist set fiveMinutes as default
      if (interval && interval.length > 0)
        this.intervalData = this.transformFilterInterval(interval)
      else this.intervalData = this.transformFilterInterval('fiveMinutes')
      // set reportType, isImportant, isUrgent, isResolved, falseAlert, isRead based on model value
      if (reportType != undefined) this.reportType = reportType
      if (isImportant != undefined) this.isImportant = isImportant
      if (isUrgent != undefined) this.isUrgent = isUrgent
      if (isResolved != undefined && isResolved) {
        this.statusCheck.all = false
        this.statusCheck = { ...this.statusCheck, resolved: true }
      } else if (isResolved != undefined && !isResolved) {
        this.statusCheck.all = false
        this.statusCheck = { ...this.statusCheck, unresolved: true }
      }
      if (falseAlert != undefined && falseAlert) {
        this.statusCheck.all = false
        this.statusCheck = { ...this.statusCheck, falseAlert: true }
      }
      if (isRead != undefined) {
        this.isRead = isRead
        this.unread = !isRead
      }
      // transform ritage to string with format loadingGeofenceId-dumpingGeofenceId
      if (ritage?.loadingGeofenceId != undefined && ritage?.dumpingGeofenceId != undefined) {
        ritage = `${ritage.loadingGeofenceId}-${ritage.dumpingGeofenceId}`
      }
      this.periodeMonth = periodeMonth
      this.summaryChart = summaryChart
      this.formValues = { ...otherFilter, ritage }
      this.fullObjects = { ...fullObjects }
      this.dateType = dateType
      await this.$nextTick()
      if (this.$refs['period-filter']) {
        this.$refs['period-filter'].SetPeriod({
          periode: this.periode,
          periodeMonth: this.periodeMonth,
          dateType: this.dateType,
        })
      }
      if (this.$refs['ritage-duration-filter']) {
        this.$refs['ritage-duration-filter'].SetRitageDuration({
          loadingDuration: this.formValues.loadingDuration,
          unloadingDuration: this.formValues.unloadingDuration,
          totalRitageDuration: this.formValues.totalRitageDuration,
          loadedTravelDuration: this.formValues.loadedTravelDuration,
          unloadedTravelDuration: this.formValues.unloadedTravelDuration,
          stoppedDurationLoaded: this.formValues.stoppedDurationLoaded,
          stoppedDurationUnloaded: this.formValues.stoppedDurationUnloaded,
        })
      }
    },
    getRequiredField(key) {
      return this.parameterFilter[key].required
    },
    updateFormValues({ key, value }) {
      this.$set(this.formValues, key, value)
    },
    updateFullObjects({ key, value }) {
      this.$set(this.fullObjects, key, value)
    },
    resetForm() {
      this.periode = []
      this.periodeMonth = null
      this.formValues = {}
      this.fullObjects = {}
      this.dateType = null
      this.intervalData = {
        fiveMinutes: true,
      }
    },
    resetFilters() {
      if (this.$refs['ritage-duration-filter']) {
        this.$refs['ritage-duration-filter'].SetRitageDuration({
          loadingDuration: null,
          unloadingDuration: null,
          totalRitageDuration: null,
          loadedTravelDuration: null,
          unloadedTravelDuration: null,
          stoppedDurationLoaded: null,
          stoppedDurationUnloaded: null,
        })
      }
    },
    cancelFilter() {
      this.resetForm()
      this.$bvModal.hide(this.modalId)
    },
    handleAccept() {
      const { ritage, ...otherFormValues } = this.formValues
      const filter = {
        periode: this.periode && this.periode.length > 0 && this.periode[0] ? this.periode : [],
        ...otherFormValues,
      }
      const filterValue = {
        periode: this.periode && this.periode.length > 0 ? this.periode : null,
        periodeMonth: this.periodeMonth ? this.periodeMonth : null,
        ...otherFormValues,
        fullObjects: this.fullObjects,
        dateType: this.dateType,
      }
      if (this.hasIntervalFilter) {
        filter.interval = this.intervalFilter
        filterValue.interval = this.intervalFilter
      } else {
        filter.interval = undefined
        filterValue.interval = undefined
      }
      if (this.hasSummaryChart) {
        filter.summaryChart = this.summaryChart
        filterValue.summaryChart = this.summaryChart
      } else {
        filter.summaryChart = undefined
        filterValue.summaryChart = undefined
      }
      if (this.hasReportTypeFilter) {
        filter.reportType = this.reportType
        filterValue.reportType = this.reportType
      } else {
        filter.reportType = undefined
        filterValue.reportType = undefined
      }
      if (this.hasIsImportantFilter && this.isImportant) {
        filter.isImportant = this.isImportant
        filterValue.isImportant = this.isImportant
      } else {
        filter.isImportant = undefined
        filterValue.isImportant = undefined
      }
      if (this.hasIsUrgentFilter && this.isUrgent) {
        filter.isUrgent = this.isUrgent
        filterValue.isUrgent = this.isUrgent
      } else {
        filter.isUrgent = undefined
        filterValue.isUrgent = undefined
      }
      if (this.hasisReadFilter && this.unread) {
        filter.isRead = false
        filterValue.isRead = false
      } else {
        filter.isRead = undefined
        filterValue.isRead = undefined
      }
      if (this.statusCheck.all) {
        filter.isResolved = undefined
        filterValue.isResolved = undefined
        filter.falseAlert = undefined
        filterValue.falseAlert = undefined
      } else if (
        this.statusCheck['unresolved'] &&
        !this.statusCheck['resolved'] &&
        !this.statusCheck['falseAlert']
      ) {
        filter.isResolved = false
        filterValue.isResolved = false
        filter.falseAlert = false
        filterValue.falseAlert = false
      } else if (
        !this.statusCheck['unresolved'] &&
        this.statusCheck['resolved'] &&
        !this.statusCheck['falseAlert']
      ) {
        filter.isResolved = true
        filterValue.isResolved = true
        filter.falseAlert = false
        filterValue.falseAlert = false
      } else if (
        !this.statusCheck['unresolved'] &&
        !this.statusCheck['resolved'] &&
        this.statusCheck['falseAlert']
      ) {
        filter.falseAlert = true
        filterValue.falseAlert = true
        filter.isResolved = undefined
        filterValue.isResolved = undefined
      } else if (
        this.statusCheck['unresolved'] &&
        this.statusCheck['resolved'] &&
        !this.statusCheck['falseAlert']
      ) {
        filter.falseAlert = false
        filterValue.falseAlert = false
        filter.isResolved = undefined
        filterValue.isResolved = undefined
      } else if (
        !this.statusCheck['unresolved'] &&
        this.statusCheck['resolved'] &&
        this.statusCheck['falseAlert']
      ) {
        filter.isResolved = true
        filterValue.isResolved = true
        filter.falseAlert = undefined
        filterValue.falseAlert = undefined
      } else if (
        this.statusCheck['unresolved'] &&
        !this.statusCheck['resolved'] &&
        this.statusCheck['falseAlert']
      ) {
        filter.falseAlert = true
        filterValue.falseAlert = true
        filter.isResolved = false
        filterValue.isResolved = false
      }
      if (ritage && Array.isArray(ritage)) {
        filter.ritage = ritage.map((item) => {
          const [loading, dumping] = item.split('-')
          return {
            loadingGeofenceId: parseInt(loading),
            dumpingGeofenceId: parseInt(dumping),
          }
        })
        filterValue.ritage = filter.ritage
      } else if (ritage && !Array.isArray(ritage)) {
        const [loading, dumping] = ritage.split('-')
        filter.ritage = {
          loadingGeofenceId: parseInt(loading),
          dumpingGeofenceId: parseInt(dumping),
        }
        filterValue.ritage = filter.ritage
      } else {
        filter.ritage = undefined
        filterValue.ritage = undefined
      }
      this.filter = filter
      if (this.validateFilterReport()) {
        let finalFilter = {}
        Object.keys(filter).forEach((key) => {
          if (filter[key] !== null) finalFilter[key] = filter[key]
        })

        if (finalFilter.vehicleId)
          finalFilter.vehicleId = Array.isArray(finalFilter.vehicleId)
            ? finalFilter.vehicleId.map((item) => parseInt(item))
            : parseInt(finalFilter.vehicleId)

        if (finalFilter.driverId)
          finalFilter.driverId = Array.isArray(finalFilter.driverId)
            ? finalFilter.driverId.map((item) => parseInt(item))
            : parseInt(finalFilter.driverId)

        if (finalFilter.geofenceId)
          finalFilter.geofenceId = Array.isArray(finalFilter.geofenceId)
            ? finalFilter.geofenceId.map((item) => parseInt(item))
            : parseInt(finalFilter.geofenceId)

        if (finalFilter.alertCode)
          finalFilter.alertCode = Array.isArray(finalFilter.alertCode)
            ? finalFilter.alertCode.map((item) => parseInt(item))
            : parseInt(finalFilter.alertCode)

        if (finalFilter.garageId)
          finalFilter.garageId = Array.isArray(finalFilter.garageId)
            ? finalFilter.garageId.map((item) => parseInt(item))
            : parseInt(finalFilter.garageId)

        if (finalFilter.startGeofenceId)
          finalFilter.startGeofenceId = Array.isArray(finalFilter.startGeofenceId)
            ? finalFilter.startGeofenceId.map((item) => parseInt(item))
            : parseInt(finalFilter.startGeofenceId)

        if (finalFilter.endGeofenceId)
          finalFilter.endGeofenceId = Array.isArray(finalFilter.endGeofenceId)
            ? finalFilter.endGeofenceId.map((item) => parseInt(item))
            : parseInt(finalFilter.endGeofenceId)

        this.$store.commit('report/updateFilter', finalFilter)
        this.$store.commit('report/updateFilterValue', filterValue)
        this.saveReportFilter(filterValue, finalFilter, this.report.key)
        this.saveFilterDetail(filterValue.fullObjects)

        if (this.report.key === 'expenses') {
          this.$store.commit('report/updateExpensesFilter', {
            transaction_date: [
              finalFilter.periode[0] + 'T00:00:00.000+07:00',
              finalFilter.periode[1] + 'T00:00:00.000+07:00',
            ],
          })
        }

        if (this.report.key === 'fleet-utilization') {
          const created_on = finalFilter.created_on
          this.$store.commit('report/updateFleetUtilizationFilter', { created_on })
        }

        this.resetForm()
        if (this.unRedirect) {
          this.$emit('refresh-table')
          this.$bvModal.hide(this.modalId)
        } else {
          const routeData = this.$router.resolve({ name: this.report.link })
          window.open(routeData.href, '_blank')
          this.$bvModal.hide(this.modalId)
        }
      }
    },
    saveFilterDetail(fullObject) {
      const keys = Object.keys(fullObject)
      const data = {}
      keys.forEach((key) => {
        if (key == 'vehicleId') {
          if (Array.isArray(fullObject.vehicleId)) {
            let licensePlate =
              fullObject.vehicleId.length > 0 ? fullObject.vehicleId[0].licensePlate : '-'
            licensePlate +=
              fullObject.vehicleId.length > 1 ? `, ${fullObject.vehicleId[1].licensePlate}` : ''
            data.licensePlate = licensePlate
          } else {
            data.licensePlate = fullObject.vehicleId.licensePlate
          }
        } else if (key == 'driverId') {
          if (Array.isArray(fullObject.driverId)) {
            let driver = fullObject.driverId.length > 0 ? fullObject.driverId[0].fullname : '-'
            driver += fullObject.driverId.length > 1 ? `, ${fullObject.driverId[1].fullname}` : ''
            data.driver = driver
          } else {
            data.driver = fullObject.driverId.fullname
          }
        } else if (key == 'garageId') {
          if (Array.isArray(fullObject.garageId)) {
            let geofence = fullObject.garageId.length > 0 ? fullObject.garageId[0].fenceName : '-'
            geofence +=
              fullObject.garageId.length > 1 ? `, ${fullObject.garageId[1].fenceName}` : ''
            data.geofence = geofence
          } else {
            data.geofence = fullObject.garageId.fenceName
          }
        } else if (key == 'intersectGeofenceIds') {
          if (Array.isArray(fullObject.intersectGeofenceIds)) {
            let geofence =
              fullObject.intersectGeofenceIds.length > 0
                ? fullObject.intersectGeofenceIds[0].fenceName
                : '-'
            geofence +=
              fullObject.intersectGeofenceIds.length > 1
                ? `, ${fullObject.intersectGeofenceIds[1].fenceName}`
                : ''
            data.geofence = geofence
          } else {
            data.geofence = fullObject.intersectGeofenceIds.fenceName
          }
        } else if (key == 'ritage') {
          if (Array.isArray(fullObject.ritage)) {
            let ritageName = fullObject.ritage.length > 0 ? fullObject.ritage[0].ritageName : '-'
            ritageName += fullObject.ritage.length > 1 ? `, ${fullObject.ritage[1].ritageName}` : ''
            data.ritageName = ritageName
          } else {
            data.ritageName = fullObject.ritage.ritageName
            data.loadingFenceName = fullObject.ritage.loadingName
            data.dumpingFenceName = fullObject.ritage.dumpingName
          }
        }
      })
      this.setReportDetail(this.report.key, { ...data })
    },
    validateFilterReport() {
      const filterReport = this.parameterFilter
      let messageError = ''
      for (const item of Object.keys(this.filter)) {
        if (
          filterReport[item] &&
          filterReport[item].required &&
          (!this.filter[item] || this.filter[item].length <= 0)
        ) {
          if (item == 'periode') {
            messageError = `${this.$t('report.object.reportPeriod')} ${this.$t(
              'global.validation.required'
            )}`
          } else if (item == 'vehicleId') {
            messageError = `${this.$t('report.fields.licensePlate')} ${this.$t(
              'global.validation.required'
            )}`
          } else if (item == 'driverId') {
            messageError = `${this.$t('report.fields.driver')} ${this.$t(
              'global.validation.required'
            )}`
          } else if (item == 'alertCode') {
            messageError = `${this.$t('report.fields.alertType')} ${this.$t(
              'global.validation.required'
            )}`
          } else if (item == 'garageId') {
            messageError = `${this.$t('report.fields.garage')} ${this.$t(
              'global.validation.required'
            )}`
          } else if (item == 'startGeofenceId') {
            messageError = `${this.$t('report.fields.startGeofence')} ${this.$t(
              'global.validation.required'
            )}`
          } else if (item == 'endGeofenceId') {
            messageError = `${this.$t('report.fields.stopGeofence')} ${this.$t(
              'global.validation.required'
            )}`
          } else if (item == 'geofenceId' && filterReport[item].required) {
            messageError = `${this.$t('report.fields.geolocation')} ${this.$t(
              'global.validation.required'
            )}`
          } else if (item == 'reportType') {
            messageError = `${this.$t('report.object.reportType.title')} ${this.$t(
              'global.validation.required'
            )}`
          } else if (item == 'loadingGeofenceId') {
            messageError = `${this.$t('report.fields.loadingGeofence')} ${this.$t(
              'global.validation.required'
            )}`
          } else if (item == 'dumpingGeofenceId') {
            messageError = `${this.$t('report.fields.dumpingGeofence')} ${this.$t(
              'global.validation.required'
            )}`
          } else if (item == 'ritage') {
            messageError = `${this.$t('report.fields.ritage')} ${this.$t(
              'global.validation.required'
            )}`
          } else if (item == 'created_on') {
            messageError = `${this.$t('report.object.reportPeriod')} ${this.$t(
              'global.validation.required'
            )}`
          }
          break
        } else if (this.hasPeriodeFilter && !this.checkReportKey('fuel-recapitulation')) {
          let date1 = moment(this.filter.periode[0])
          let date2 = moment(this.filter.periode[1])
          let diff = Math.abs(date1.diff(date2, 'days')) + 1

          if (
            !this.hasCsvScope &&
            this.checkReportKey('history-vehicle') &&
            item == 'vehicleId' &&
            this.filter.vehicleId.length > 1 &&
            diff > 7
          ) {
            messageError = this.$t('global.validation.7dayPeriod')
            break
          }
          if (
            this.hasIntervalFilter &&
            (this.checkReportKey('temperature') ||
              this.checkReportKey('detail-history-vehicle') ||
              this.checkReportKey('fuel')) &&
            this.filter.vehicleId &&
            this.filter.vehicleId.length > 1 &&
            this.filter.interval &&
            this.filter.interval === 'all'
          ) {
            messageError = this.$t('report.text.intervalValid')
            break
          }
        } else if (
          item == 'loadingDuration' ||
          item == 'unloadingDuration' ||
          item == 'totalRitageDuration' ||
          item == 'loadedTravelDuration' ||
          item == 'unloadedTravelDuration' ||
          item == 'stoppedDurationLoaded' ||
          item == 'stoppedDurationUnloaded'
        ) {
          if (
            (this.filter[item].hours <= 0 &&
              this.filter[item].minutes <= 0 &&
              this.filter[item].type) ||
            ((this.filter[item].hours || this.filter[item].minutes) && !this.filter[item].type)
          ) {
            messageError = `${this.$t('report.fields.' + item)} ${this.$t(
              'global.validation.complete'
            )}`
            break
          }
        }
      }
      if (
        this.checkReportKey('alert') &&
        this.filter.vehicleId &&
        this.filter.vehicleId.length > 20 &&
        this.filter.alertCode &&
        this.filter.alertCode.length > 1
      ) {
        messageError = this.$t('report.text.vehicleAlertSwal')
      }
      if (messageError.length > 0) {
        this.$mcToast.error(messageError)
        return false
      }
      return true
    },

    transformFilterInterval(interval) {
      const intervalData = {}
      intervalData[interval] = true
      return intervalData
    },

    filterIntervalData(interval) {
      this.intervalData = {}
      this.intervalData[interval] = true
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.modal-manage-view) {
  .modal-dialog {
    border-radius: 0px;
    max-height: 100vh;
    height: 100%;
    margin: 0;
    margin-left: auto;
  }

  .modal-content {
    height: 100%;
  }

  .modal-content,
  .modal-header,
  .modal-footer {
    border-radius: 0;

    .modal__footer-btn {
      display: flex;
    }
  }

  .modal-header {
    color: $white;
    justify-content: space-between;

    .__reset {
      font-size: 13px;
      text-align: right;
      cursor: pointer;
    }
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
    min-height: 0; /* critical! without this, flex won't shrink */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
  }

  .modal-body::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }

  .wrapper {
    // overflow-y: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

.section {
  :deep(input[type='checkbox']) {
    margin-bottom: 2px;
  }

  & > p {
    color: $teal-300;
    font-weight: 600;
  }

  &-information {
    &__content {
      border: 2px solid $blue-100;
      border-radius: 4px;
      margin-bottom: 20px;

      & > p {
        display: flex;
        gap: 5px;
        align-items: center;
        margin-bottom: 0;

        &:first-child {
          margin-bottom: 4px;
        }
      }
    }
  }

  &-column-manager {
    overflow: hidden;
    flex: 1 1 auto;

    &__content {
      box-shadow: 0px 5px 15px 0px rgba(13, 68, 145, 0.08);
      overflow-y: hidden;
      height: 100%;
    }

    &__header {
      button {
        background: transparent;
        border: none;
        color: $cyan;
        font-weight: 600;
      }
    }

    &__draggable {
      background: $gray;
      padding: 16px;
      overflow-y: auto;
      height: 100%;
      padding-bottom: 86px;

      &-item {
        cursor: move;
        background: white;
        margin-bottom: 16px;
        border-radius: 5px;
        padding: 14px 12px;
        transition: 0.3s;

        .__divider {
          background-color: $teal-100;
          height: 16px;
          width: 1px;
        }
      }
    }
  }
}

.icon-outline_move {
  color: $blue-400;
  font-size: 16px;
}

.reset-button {
  background: transparent;
  border: none;
  color: $white;
  float: right;
}
</style>
